import { useMemo, useState } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Divider,
  Grid,
  MenuItem,
  Select,
  SelectChangeEvent,
  Stack,
  Typography,
} from "@mui/material";
import InsertChartOutlinedIcon from "@mui/icons-material/InsertChartOutlined";
import DownloadOutlinedIcon from "@mui/icons-material/DownloadOutlined";
import RefreshOutlinedIcon from "@mui/icons-material/RefreshOutlined";

const BRAND = {
  primary: "#2563eb",
  primarySoft: "rgba(37,99,235,0.08)",
  accent: "#1d4ed8",
  surface: "#f8fafc",
  border: "#e2e8f0",
  text: "#0f172a",
  muted: "#64748b",
  neutral: "#475569",
  success: "#16a34a",
  warning: "#d97706",
  info: "#0ea5e9",
};

type FilterKey = "region" | "customer" | "project" | "timeframe";

const PREDEFINED_REPORTS = [
  {
    key: "conversion",
    title: "RFQ → Booking Conversion",
    value: "64%",
    helper: "+5% vs last month",
    trend: "positive",
  },
  {
    key: "onTime",
    title: "On-time Delivery",
    value: "91%",
    helper: "Target 95%",
    trend: "neutral",
  },
  {
    key: "collections",
    title: "Payment Collection Performance",
    value: "87%",
    helper: "Average DSO: 18 days",
    trend: "positive",
  },
  {
    key: "activity",
    title: "Customer Activity Trends",
    value: "342",
    helper: "Active accounts this month",
    trend: "neutral",
  },
];

const KPI_VISUALS = [
  {
    label: "Conversion Trend",
    value: "↑ 6 pts",
    color: BRAND.primary,
    data: [48, 52, 55, 61, 64],
  },
  {
    label: "On-time Delivery",
    value: "↗ 3 pts",
    color: BRAND.info,
    data: [82, 84, 85, 88, 91],
  },
  {
    label: "Payment Collection",
    value: "↓ 2 days",
    color: BRAND.accent,
    data: [24, 22, 21, 20, 18],
  },
];

const REGIONS = ["All Regions", "Dubai", "Abu Dhabi", "Northern Emirates"];
const CUSTOMERS = ["All Customers", "Acme LLC", "BuildCo", "Skyline"];
const PROJECTS = ["All Projects", "Marina Tower", "Expo Villas", "Harbour Point"];
const TIMEFRAMES = [
  { label: "Last 7 days", value: "7d" },
  { label: "Last 30 days", value: "30d" },
  { label: "Quarter to date", value: "qtd" },
  { label: "Year to date", value: "ytd" },
];
const EXPORT_FORMATS = ["Excel", "CSV", "PDF"];

const ReportsPage = () => {
  const [filters, setFilters] = useState<Record<FilterKey, string>>({
    region: REGIONS[0],
    customer: CUSTOMERS[0],
    project: PROJECTS[0],
    timeframe: TIMEFRAMES[1].value,
  });
  const [exportFormat, setExportFormat] = useState(EXPORT_FORMATS[0]);

  const filteredReports = useMemo(() => {
    // Placeholder for future filtering logic / API integration
    return PREDEFINED_REPORTS;
  }, [filters]);

  const handleFilterChange = (key: FilterKey) => (event: SelectChangeEvent) => {
    setFilters((prev) => ({ ...prev, [key]: event.target.value }));
  };

  return (
    <>
      <div className="main_title">
        <h1>Reports &amp; Analytics</h1>
        <p>Monitor funnel health, track delivery KPIs, and build custom exports.</p>
      </div>

      <div className="cards">
        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" sx={{ fontWeight: 700, color: BRAND.text }}>
            Predefined Reports
          </Typography>
          <Typography sx={{ color: BRAND.muted, fontSize: "0.9rem" }}>
            Quick snapshots for the KPIs you review every week.
          </Typography>
        </Box>

        <Grid container spacing={2.5}>
          {filteredReports.map((report) => (
            <Grid key={report.key} item xs={12} sm={6} md={3}>
              <Card
                sx={{
                  borderRadius: 3,
                  border: `1px solid ${BRAND.border}`,
                  boxShadow: "0 20px 45px rgba(15,23,42,0.08)",
                  height: "100%",
                  background: "linear-gradient(135deg, #ffffff, #f9fbff)",
                }}
              >
                <CardContent>
                  <Stack direction="row" spacing={1} alignItems="center" mb={1.5}>
                    <InsertChartOutlinedIcon sx={{ color: BRAND.primary }} />
                    <Typography sx={{ fontSize: "0.85rem", fontWeight: 600, color: BRAND.neutral }}>
                      {report.title}
                    </Typography>
                  </Stack>

                  <Typography sx={{ fontSize: "2rem", fontWeight: 700, color: BRAND.text }}>
                    {report.value}
                  </Typography>
                  <Chip
                    size="small"
                    label={report.helper}
                    sx={{
                      mt: 1.25,
                      fontWeight: 600,
                      borderRadius: 2,
                      backgroundColor:
                        report.trend === "positive"
                          ? "rgba(22,163,74,0.12)"
                          : report.trend === "negative"
                          ? "rgba(220,38,38,0.12)"
                          : BRAND.primarySoft,
                      color:
                        report.trend === "positive"
                          ? BRAND.success
                          : report.trend === "negative"
                          ? "#b91c1c"
                          : BRAND.neutral,
                    }}
                  />
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>

        <Divider sx={{ my: 4 }} />

        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" sx={{ fontWeight: 700, color: BRAND.text }}>
            Custom Reports
          </Typography>
          <Typography sx={{ color: BRAND.muted, fontSize: "0.9rem" }}>
            Combine filters across region, project, and timeframe to export exactly what you need.
          </Typography>
        </Box>

        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={3}>
            <Select
              fullWidth
              value={filters.region}
              onChange={handleFilterChange("region")}
              size="small"
              sx={{
                backgroundColor: BRAND.surface,
                '& .MuiOutlinedInput-notchedOutline': { borderColor: BRAND.border },
                '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: BRAND.accent },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: BRAND.primary },
              }}
            >
              {REGIONS.map((region) => (
                <MenuItem key={region} value={region}>
                  {region}
                </MenuItem>
              ))}
            </Select>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Select
              fullWidth
              value={filters.customer}
              onChange={handleFilterChange("customer")}
              size="small"
              sx={{
                backgroundColor: BRAND.surface,
                '& .MuiOutlinedInput-notchedOutline': { borderColor: BRAND.border },
                '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: BRAND.accent },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: BRAND.primary },
              }}
            >
              {CUSTOMERS.map((customer) => (
                <MenuItem key={customer} value={customer}>
                  {customer}
                </MenuItem>
              ))}
            </Select>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Select
              fullWidth
              value={filters.project}
              onChange={handleFilterChange("project")}
              size="small"
              sx={{
                backgroundColor: BRAND.surface,
                '& .MuiOutlinedInput-notchedOutline': { borderColor: BRAND.border },
                '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: BRAND.accent },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: BRAND.primary },
              }}
            >
              {PROJECTS.map((project) => (
                <MenuItem key={project} value={project}>
                  {project}
                </MenuItem>
              ))}
            </Select>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Select
              fullWidth
              value={filters.timeframe}
              onChange={handleFilterChange("timeframe")}
              size="small"
              sx={{
                backgroundColor: BRAND.surface,
                '& .MuiOutlinedInput-notchedOutline': { borderColor: BRAND.border },
                '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: BRAND.accent },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: BRAND.primary },
              }}
            >
              {TIMEFRAMES.map((timeframe) => (
                <MenuItem key={timeframe.value} value={timeframe.value}>
                  {timeframe.label}
                </MenuItem>
              ))}
            </Select>
          </Grid>
        </Grid>

        <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} mt={3}>
          <Button
            variant="contained"
            startIcon={<RefreshOutlinedIcon />}
            sx={{
              textTransform: "none",
              borderRadius: 2,
              fontWeight: 600,
              background: `linear-gradient(135deg, ${BRAND.primary}, ${BRAND.accent})`,
              boxShadow: "0 15px 30px rgba(37,99,235,0.25)",
            }}
          >
            Run Report
          </Button>

          <Select
            value={exportFormat}
            onChange={(e) => setExportFormat(e.target.value)}
            size="small"
            sx={{
              width: { xs: "100%", sm: 180 },
              backgroundColor: BRAND.surface,
              '& .MuiOutlinedInput-notchedOutline': { borderColor: BRAND.border },
              '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: BRAND.accent },
              '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: BRAND.primary },
            }}
          >
            {EXPORT_FORMATS.map((format) => (
              <MenuItem key={format} value={format}>
                {format}
              </MenuItem>
            ))}
          </Select>

          <Button
            variant="outlined"
            startIcon={<DownloadOutlinedIcon />}
            sx={{
              textTransform: "none",
              borderRadius: 2,
              fontWeight: 600,
              color: BRAND.primary,
              borderColor: BRAND.primary,
              '&:hover': {
                borderColor: BRAND.accent,
                color: BRAND.accent,
              },
            }}
          >
            Export {exportFormat}
          </Button>
        </Stack>

        <Divider sx={{ my: 4 }} />

        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" sx={{ fontWeight: 700, color: BRAND.text }}>
            KPI Visualizations
          </Typography>
          <Typography sx={{ color: BRAND.muted, fontSize: "0.9rem" }}>
            Sparkline summaries to track momentum across critical metrics.
          </Typography>
        </Box>

        <Grid container spacing={3}>
          {KPI_VISUALS.map((kpi) => (
            <Grid item xs={12} md={4} key={kpi.label}>
              <Card
                sx={{
                  borderRadius: 3,
                  border: `1px solid ${BRAND.border}`,
                  boxShadow: "0 15px 40px rgba(15,23,42,0.06)",
                  backgroundColor: "#fff",
                }}
              >
                <CardContent>
                  <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1}>
                    <Typography sx={{ fontWeight: 600, color: BRAND.text }}>{kpi.label}</Typography>
                    <Chip
                      size="small"
                      label={kpi.value}
                      sx={{
                        fontWeight: 600,
                        backgroundColor: BRAND.primarySoft,
                        color: BRAND.accent,
                      }}
                    />
                  </Stack>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "flex-end",
                      gap: 1,
                      height: 120,
                    }}
                  >
                    {kpi.data.map((value, idx) => (
                      <Box
                        key={`${kpi.label}-${idx}`}
                        sx={{
                          flex: 1,
                          borderRadius: 2,
                          background: `linear-gradient(180deg, ${kpi.color}, rgba(37,99,235,0.1))`,
                          height: `${value}%`,
                          minHeight: 20,
                        }}
                      />
                    ))}
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </div>
    </>
  );
};

export default ReportsPage;

